import React, { useEffect, useState } from "react";
import Image from "../../../elements/Image";
import Button from "../../../form/Button";
import Select from "../../../form/select/Select";
import { Link, useNavigate } from "react-router-dom";
import Tags from "../../../form/Tags";
import Radio from "../../../form/Radio";
import { classNames } from "../../../../helpers/classNames";
import Input from "../../../form/Input";
import MultiselectDropdown from "../../../form/MultiselectDropdown";
import NoImage from "../../../../assets/images/no-event-image-placeholder-2x.png";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { categoryList } from "../../../../services/categoryService";
import { lngDetect } from "../../../../helpers/lngDetect";
import { organizationList } from "../../../../services/organizationService";
import { toast } from "react-toastify";
import useForm from "../../../../hooks/useForm";
import EventInfoPreview from "./EventInfoPreview";
import Form from "../../../form/Form";
import { useSelector } from "react-redux";
import { eventAddEdit } from "../../../../services/eventService";
import { collectionList } from "../../../../services/collectionService";
import { useOrganization } from "../../../../context/OrganizationContext";
import { useDebounce } from "../../../../hooks/useDebounce";
import EventCatType from "../event-manage/EventCatType";
import PublishSetting from "../event-manage/PublishSetting";
import SearchSetting from "../event-manage/SearchSetting";
import { t } from "i18next";
import LangSwitch from "../../../elements/LangSwitch";

let validation = {
  type: { required: true, message: "type is required" },
  category: { required: true, message: "category is required" },
  organization: { required: true, message: "organization is required" },
  daysBeforeRefund: {
    type: "number",
    errorType: "info",
    max: 30,
    min: 1,
    message: "Value should be set (1-30)",
  },
};

const Preview = ({ event = {} }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [keyword, setKeyword] = useState("");
  const collectionSearch = useDebounce(keyword, 300);
  const { lang } = useSelector((state) => state?.langProvider);
  const [selectedLang, setSelectedLang] = useState(lang || "en");

  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    formValidate,
  } = useForm({ validationRules: validation });

  const setFieldsValues = ({ data }) => {
    setFieldsValue({
      type: data?.categoryData
        ?.filter((item) => item?.type === "event-type")
        .at(-1)?._id,
      category: data?.categoryData
        ?.filter((item) => item?.type === "event-category")
        .at(-1)?._id,
      organization: data?.organizationData?._id,
      tags: data?.tagData?.map((item) => ({
        label: item?.name?.en || "",
        value: item?._id,
      })),
      visibility: data?.visibility,
      // password: data?.password,
      allowRefundPolicy: data?.refund?.allowRefundPolicy,
      daysBeforeRefund: data?.refund?.daysBeforeRefund,
      autoRefund: data?.refund?.autoRefund,
      collections: data?.collectionData?.map((item) => item?._id),
      collectionData: data?.collectionData,
      hasPassword: data?.hasPassword,
    });
  };

  useEffect(() => {
    if (event?._id) {
      if (event?.hasPassword) {
        delete validation?.password;
      } else if (event?.visibility === "private") {
        validation["password"] = {
          required: true,
          message: "Password is required",
        };
      }
      setFieldsValues({ data: event });
    }
  }, [event]);

  const {
    mutate: publishHandler,
    error,
    isPending,
  } = useMutation({
    mutationFn: async (val) => {
      const payload = {
        ...val,
        collections: val?.collections,
        categories: [val?.type, val?.category],
        tags:
          val?.tags?.length === 0
            ? ""
            : val?.tags?.map((tag, i) => ({
                name: { en: tag?.label },
              })),
        ...(val?.visibility === "private" && { password: val?.password }),
        ...(val?.visibility === "public" && { password: "" }),
        refund: {
          allowRefundPolicy: val?.allowRefundPolicy,
          daysBeforeRefund: val?.daysBeforeRefund,
          // autoRefund: val?.autoRefund,
          autoRefund: true,
        },
        ...(event?._id && { id: event?._id }),
        contentType: "application/json",
      };

      delete payload?.hasPassword;
      return await eventAddEdit(payload);
    },
    onSuccess: (res) => {
      if (res?.status === 200 || res?.status === 201) {
        if (res?.status === 201) {
          navigate(`/${lang}/user/events/event-manage/${res?.data?.uid}`);
        }
        if (res?.status === 200) {
          setFieldsValues({ data: res?.data });
          queryClient.setQueryData(["event-details", event?.uid], (state) => {
            const state_data = JSON.parse(JSON.stringify(state || {}));
            state_data.data = res.data;
            return state_data;
          });
          // navigate(`/${lang}/user/events`);
        }
      } else {
        toast.error(res.message || res?.error);
      }
    },
  });

  return (
    <Form handleSubmit={handleSubmit} onSubmit={publishHandler}>
      <div className="bg-white py-3 md:py-6 px-2 md:px-6 rounded-md space-y-6">
        {/* <LangSwitch
          langs={["en", "es"]}
          selectedLang={selectedLang}
          setSelectedLang={setSelectedLang}
        /> */}
        <div className="py-4 md:py-8 px-3 md:px-6 rounded-xl space-y-4 border border-gray-200">
          <EventInfoPreview data={event} />
          <div className="bg-slate-100 rounded-xl px-4 py-4">
            <div className="text-lg text-black font-bold">
              {t("Event type and category")}
            </div>
            <div className="text-sm text-slate-500 font-medium">
              {t(
                "Your type and category help your event appear in more searches."
              )}
            </div>
            <EventCatType
              errors={errors}
              formValidate={formValidate}
              handleChange={handleChange}
              values={values}
              setFieldsValue={setFieldsValue}
            />
          </div>
        </div>
        <div className="py-4 md:py-8 px-3 md:px-6 rounded-xl space-y-4 border border-gray-200">
          <h4 className="text-xl md:text-3xl text-deep-purple font-bold mb-3">
            {t("Publish settings")}
          </h4>
          <PublishSetting
            errors={errors}
            formValidate={formValidate}
            handleChange={handleChange}
            values={values}
            setFieldsValue={setFieldsValue}
            event={event}
            validation={validation}
          />
        </div>
        <div className="py-4 md:py-8 px-3 md:px-6 rounded-xl space-y-4 border border-gray-200">
          <h4 className="text-xl md:text-3xl text-deep-purple font-bold mb-3">
            {t("Search settings")}
          </h4>
          <SearchSetting
            errors={errors}
            formValidate={formValidate}
            handleChange={handleChange}
            values={values}
            setFieldsValue={setFieldsValue}
          />
        </div>
      </div>
      <div className="flex mt-7 gap-3 justify-end bg-magnolia py-4 sticky bottom-0 right-0 z-[2] pr-6">
        <Button
          buttonType="submit"
          buttonLabel={t("update event")}
          {...(isPending && {
            loading: true,
            buttonIconPosition: "left",
            isDisable: true,
          })}
        />
      </div>
    </Form>
  );
};

export default Preview;
